import type { AssetDefenderBadges } from '@/types/oscar';

export interface RowBadge { text: string; tone: 'ok' | 'warn' | 'muted'; title?: string }

/**
 * The one short label a register row shows in its Defender column.
 *
 * <p>An Azure Arc server shows what Arc knows (the sensor is deployed or it is not); a Windows device Intune
 * reports on shows Intune's view. A server is never also given a laptop's label, so Arc wins when both exist.</p>
 *
 * <p>⚠ The words are "sensor deployed" and "healthy (per Intune)", never "protected": neither is a risk score. A
 * server that has left Arc is not shown as having a sensor. No entry at all is no label (most assets).</p>
 */
export function rowBadge(assetId: number, badges: AssetDefenderBadges | undefined): RowBadge | null {
  if (!badges) return null;

  const arc = badges.Arc.find((a) => a.AssetId === assetId);
  if (arc) {
    if (!arc.IsPresent) return { text: 'Left Arc', tone: 'warn', title: 'No longer in Azure Arc' };
    if (!arc.DefenderInstalled) return { text: 'No sensor', tone: 'warn', title: 'In Azure Arc, but the Defender extension is not installed' };
    const connected = !arc.ArcStatus || arc.ArcStatus.toLowerCase() === 'connected';
    return connected
      ? { text: 'Sensor deployed', tone: 'ok', title: 'Defender extension deployed via Azure Arc (not a health verdict)' }
      : { text: 'Sensor deployed', tone: 'warn', title: `Defender extension deployed, but Arc is ${arc.ArcStatus}` };
  }

  const d = badges.Defender.find((x) => x.AssetId === assetId);
  if (d) {
    if (d.Health === 'healthy') return { text: 'Healthy', tone: 'ok', title: 'Defender healthy, per Intune (not a risk score)' };
    if (d.Health === 'attention') return { text: 'Needs attention', tone: 'warn', title: d.HealthReasons ?? undefined };
    return { text: 'Not reported', tone: 'muted', title: d.HealthReasons ?? 'Intune has no Defender report for this device' };
  }
  return null;
}
